import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Label,
  Modal,
  Popup,
  Table,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess, showWarning } from '../helpers';
import globalStyles from '../styles/globalStyles';
import ProxyNodesModal from './ProxyNodesModal';
import ProxySubscriptionModal from './ProxySubscriptionModal';

export const filterProxySubscriptions = (items, search) => {
  const needle = (search || '').trim().toLowerCase();
  if (!needle) return items || [];
  return (items || []).filter((item) =>
    `${item.id} ${item.name} ${item.url_masked} ${item.detected_format}`
      .toLowerCase()
      .includes(needle)
  );
};

export const formatProxyTimestamp = (value) =>
  value
    ? new Date(
        typeof value === 'number' && value < 1e12 ? value * 1000 : value
      ).toLocaleString()
    : '-';

export const formatProxyMetric = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0';

export const formatProxyResponseTime = (value) =>
  value === null || value === undefined || value === ''
    ? '-'
    : `${Number(value).toLocaleString()} ms`;

export const summarizeProxyFetchAll = (data) => {
  const results = Array.isArray(data?.results) ? data.results : [];
  return {
    results,
    successCount: data?.success_count ?? 0,
    failedCount: data?.failed_count ?? 0,
    failures: results.filter((result) => !result.success),
  };
};

export const getProxySubscriptionStats = (items) => ({
  total: (items || []).length,
  enabled: (items || []).filter((item) => item.enabled).length,
  nodes: (items || []).reduce((sum, item) => sum + (item.node_count || 0), 0),
});

const ChannelProxySetting = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(0);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const nodesRequestRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/proxy-subscription');
      if (!res.data?.success)
        throw new Error(
          res.data?.message ||
            t('setting.proxy_subscription.messages.load_failed')
        );
      setItems(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (method, path, data, successKey) => {
    try {
      const res = await API[method](path, data);
      if (!res.data?.success)
        throw new Error(
          res.data?.message ||
            t('setting.proxy_subscription.messages.operation_failed')
        );
      showSuccess(t(successKey));
      await load();
      return true;
    } catch (error) {
      showError(error);
      return false;
    }
  };

  const rowAction = async (item, run) => {
    if (busyId || fetchingAll) return false;
    setBusyId(item.id);
    try {
      return await run();
    } finally {
      setBusyId(0);
    }
  };

  const save = async (data) => {
    const editing = modal?.type === 'edit';
    setSaving(true);
    const ok = await action(
      editing ? 'put' : 'post',
      '/api/proxy-subscription',
      editing ? { ...data, id: modal.item.id } : data,
      editing
        ? 'setting.proxy_subscription.messages.updated'
        : 'setting.proxy_subscription.messages.created'
    );
    setSaving(false);
    if (ok) setModal(null);
  };

  const showNodes = async (item) => {
    const requestId = ++nodesRequestRef.current;
    setModal({ type: 'nodes', item });
    setNodes([]);
    setNodesLoading(true);
    try {
      const res = await API.get(`/api/proxy-subscription/${item.id}/nodes`);
      if (requestId !== nodesRequestRef.current) return;
      if (!res.data?.success)
        throw new Error(
          res.data?.message ||
            t('setting.proxy_subscription.messages.nodes_failed')
        );
      setNodes(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (error) {
      if (requestId === nodesRequestRef.current) showError(error);
    } finally {
      if (requestId === nodesRequestRef.current) setNodesLoading(false);
    }
  };

  const closeNodes = () => {
    nodesRequestRef.current += 1;
    setModal(null);
    setNodes([]);
    setNodesLoading(false);
  };

  const fetchOne = (item) =>
    rowAction(item, async () => {
      try {
        const res = await API.post(`/api/proxy-subscription/${item.id}/fetch`);
        if (!res.data?.success) throw new Error(res.data?.message);
        const result = res.data.data || {};
        showSuccess(
          t('setting.proxy_subscription.messages.fetch_one_summary', {
            count: result.node_count ?? 0,
            skipped: result.skipped ?? 0,
            format: result.format || '-',
          })
        );
      } catch (error) {
        showError(error);
      }
      await load();
    });

  const toggle = (item) =>
    rowAction(item, () =>
      action(
        'put',
        `/api/proxy-subscription/${item.id}/status`,
        { enabled: !item.enabled },
        'setting.proxy_subscription.messages.updated'
      )
    );
  const makeCurrent = (item) =>
    rowAction(item, () =>
      action(
        'put',
        `/api/proxy-subscription/${item.id}/current`,
        null,
        'setting.proxy_subscription.messages.current_set'
      )
    );
  const remove = (item) =>
    rowAction(item, () =>
      action(
        'delete',
        `/api/proxy-subscription/${item.id}`,
        null,
        'setting.proxy_subscription.messages.deleted'
      )
    );

  const fetchAll = async () => {
    if (fetchingAll || busyId) return;
    setFetchingAll(true);
    try {
      const res = await API.post('/api/proxy-subscription/fetch-all');
      if (!res.data?.success) throw new Error(res.data?.message);
      const { results, successCount, failedCount, failures } =
        summarizeProxyFetchAll(res.data.data || {});
      if (!results.length) {
        showWarning(t('setting.proxy_subscription.messages.fetch_all_empty'));
      } else {
        const summary = t(
          'setting.proxy_subscription.messages.fetch_all_summary',
          {
            success: successCount,
            failed: failedCount,
          }
        );
        failedCount ? showWarning(summary) : showSuccess(summary);
        failures.forEach((result) =>
          showError(
            t('setting.proxy_subscription.messages.fetch_all_failure', {
              name: result.name,
              reason:
                result.message ||
                t('setting.proxy_subscription.messages.operation_failed'),
            })
          )
        );
      }
      await load();
    } catch (error) {
      showError(error);
    } finally {
      setFetchingAll(false);
    }
  };

  const visible = useMemo(
    () => filterProxySubscriptions(items, search),
    [items, search]
  );
  const stats = getProxySubscriptionStats(items);
  const rowBusy = (item) => busyId === item.id || fetchingAll;

  const moreMenu = (item) => (
    <Dropdown
      button
      compact
      icon='ellipsis horizontal'
      className='proxy-more-menu'
      disabled={rowBusy(item)}
      aria-label={t('setting.proxy_subscription.more')}
    >
      <Dropdown.Menu direction='left'>
        <Dropdown.Item
          icon={item.enabled ? 'pause' : 'play'}
          text={
            item.enabled
              ? t('setting.proxy_subscription.disable')
              : t('setting.proxy_subscription.enable')
          }
          onClick={() => toggle(item)}
        />
        <Dropdown.Item
          icon='check circle outline'
          text={t('setting.proxy_subscription.set_current')}
          disabled={item.is_current || !item.enabled}
          onClick={() => makeCurrent(item)}
        />
        <Dropdown.Item
          icon='edit outline'
          text={t('setting.proxy_subscription.edit')}
          onClick={() => setModal({ type: 'edit', item })}
        />
        <Dropdown.Divider />
        <Dropdown.Item
          icon='trash alternate outline'
          text={t('setting.proxy_subscription.delete')}
          className='proxy-danger-item'
          onClick={() => setDeleteTarget(item)}
        />
      </Dropdown.Menu>
    </Dropdown>
  );

  const usageStats = (item, className = '') => (
    <div className={`proxy-usage-stats ${className}`.trim()}>
      {[
        ['request_count', formatProxyMetric(item.request_count)],
        ['input_tokens', formatProxyMetric(item.input_tokens)],
        ['output_tokens', formatProxyMetric(item.output_tokens)],
        ['total_tokens', formatProxyMetric(item.total_tokens)],
        ['avg_response_time', formatProxyResponseTime(item.avg_response_time)],
        ['last_response_time', formatProxyResponseTime(item.last_response_time)],
      ].map(([key, value]) => (
        <span key={key}>
          <b>{value}</b>
          {t(`setting.proxy_subscription.metrics.${key}`)}
        </span>
      ))}
    </div>
  );

  const actions = (item) => (
    <div className='proxy-row-actions'>
      <Button
        size='mini'
        disabled={rowBusy(item)}
        onClick={() => showNodes(item)}
        aria-label={t('setting.proxy_subscription.nodes_label', {
          name: item.name,
        })}
      >
        <Icon name='sitemap' /> {t('setting.proxy_subscription.nodes')}
      </Button>
      <Button
        size='mini'
        loading={busyId === item.id}
        disabled={rowBusy(item)}
        onClick={() => fetchOne(item)}
        aria-label={t('setting.proxy_subscription.fetch_label', {
          name: item.name,
        })}
      >
        <Icon name='sync' /> {t('setting.proxy_subscription.fetch')}
      </Button>
      {moreMenu(item)}
    </div>
  );

  const emptyKey = loading
    ? 'loading'
    : items.length && search.trim()
    ? 'no_search'
    : 'empty';

  const renderEmptyState = () => (
    <div className={`proxy-empty-state is-${emptyKey}`}>
      <div className='proxy-empty-icon'>
        <Icon
          name={loading ? 'circle notch' : search.trim() ? 'search' : 'inbox'}
          loading={loading}
        />
      </div>
      <strong>{t(`setting.proxy_subscription.states.${emptyKey}.title`)}</strong>
      <span>
        {t(`setting.proxy_subscription.states.${emptyKey}.description`)}
      </span>
      {emptyKey === 'empty' && (
        <>
          <div className='proxy-empty-features'>
            <span>
              <Icon name='lock' />
              {t('setting.proxy_subscription.states.empty.feature_encrypted')}
            </span>
            <span>
              <Icon name='sitemap' />
              {t('setting.proxy_subscription.states.empty.feature_autoparse')}
            </span>
            <span>
              <Icon name='heartbeat' />
              {t('setting.proxy_subscription.states.empty.feature_healthcheck')}
            </span>
          </div>
          <div className='proxy-empty-actions'>
            <Button primary onClick={() => setModal({ type: 'create' })}>
              <Icon name='plus' />
              {t('setting.proxy_subscription.add')}
            </Button>
          </div>
          <small className='proxy-empty-note'>
            {t('setting.proxy_subscription.states.empty.hint')}
          </small>
        </>
      )}
      {emptyKey === 'no_search' && (
        <div className='proxy-empty-actions'>
          <Button onClick={() => setSearch('')}>
            <Icon name='erase' />
            {t('setting.proxy_subscription.states.no_search.clear')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className='settings-setting-view settings-channel-proxy'>
      <section className='proxy-subscription-overview'>
        <div>
          <span className='studio-section-eyebrow'>PROXY INVENTORY</span>
          <h3>{t('setting.proxy_subscription.title')}</h3>
          <p>{t('setting.proxy_subscription.subtitle')}</p>
        </div>
        <div className='proxy-overview-stats'>
          <div>
            <strong>{stats.total}</strong>
            <span>{t('setting.proxy_subscription.stats.total')}</span>
          </div>
          <div>
            <strong>{stats.enabled}</strong>
            <span>{t('setting.proxy_subscription.stats.enabled')}</span>
          </div>
          <div>
            <strong>{stats.nodes}</strong>
            <span>{t('setting.proxy_subscription.stats.nodes')}</span>
          </div>
        </div>
      </section>

      <section className='proxy-subscription-controls'>
        <div className='proxy-controls-heading'>
          <div>
            <span className='studio-section-eyebrow'>PROXY CONTROLS</span>
            <h4>{t('setting.proxy_subscription.controls')}</h4>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <div className='proxy-controls-body'>
          <Form.Input
            icon='search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('setting.proxy_subscription.search')}
            aria-label={t('setting.proxy_subscription.search_label')}
          />
          <div className='proxy-controls-actions'>
            <Button
              onClick={load}
              loading={loading}
              disabled={loading || fetchingAll}
            >
              <Icon name='refresh' />
              {t('setting.proxy_subscription.refresh')}
            </Button>
            <Button
              onClick={fetchAll}
              loading={fetchingAll}
              disabled={fetchingAll || !!busyId}
            >
              <Icon name='sync' />
              {t('setting.proxy_subscription.fetch_all')}
            </Button>
            <Button
              primary
              onClick={() => setModal({ type: 'create' })}
              disabled={fetchingAll}
            >
              <Icon name='plus' />
              {t('setting.proxy_subscription.add')}
            </Button>
          </div>
        </div>
      </section>

      <section className='proxy-subscription-inventory'>
        <div className='proxy-inventory-head'>
          <div>
            <span className='studio-section-eyebrow'>SUBSCRIPTIONS</span>
            <h4>{t('setting.proxy_subscription.inventory')}</h4>
          </div>
          <span className='studio-record-count'>{visible.length}</span>
        </div>
        {visible.length ? (
        <div className='proxy-desktop-table'>
          <Table basic='very' compact className='proxy-subscription-table'>
            <Table.Header>
              <Table.Row>
                {[
                  'subscription',
                  'status',
                  'profile',
                  'usage',
                  'last_fetch',
                  'health',
                  'actions',
                ].map((key) => (
                  <Table.HeaderCell key={key} style={globalStyles.tableHeader}>
                    {t(`setting.proxy_subscription.table.${key}`)}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {visible.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <div className='proxy-subscription-primary'>
                        <div>
                          <strong>{item.name}</strong>
                          {item.is_current && (
                            <Label size='tiny' color='black'>
                              {t('setting.proxy_subscription.current')}
                            </Label>
                          )}
                        </div>
                        <code>{item.url_masked || '-'}</code>
                        <small>#{item.id}</small>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Label
                        basic
                        size='tiny'
                        color={item.enabled ? 'green' : 'grey'}
                      >
                        {item.enabled
                          ? t('setting.proxy_subscription.enabled')
                          : t('setting.proxy_subscription.disabled')}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{item.detected_format || '-'}</strong>
                      <span className='proxy-cell-meta'>
                        {t('setting.proxy_subscription.node_count', {
                          count: item.node_count ?? 0,
                        })}
                        {item.last_skipped_node_count > 0 && (
                          <Popup
                            trigger={
                              <Label basic size='tiny' color='orange'>
                                {item.last_skipped_node_count}
                              </Label>
                            }
                            content={t(
                              'setting.proxy_subscription.skipped_hint',
                              { count: item.last_skipped_node_count }
                            )}
                          />
                        )}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{usageStats(item)}</Table.Cell>
                    <Table.Cell>
                      {formatProxyTimestamp(item.last_fetch_time)}
                    </Table.Cell>
                    <Table.Cell>
                      {item.last_error ? (
                        <span className='proxy-health-error'>
                          <Icon name='warning sign' />
                          {item.last_error}
                        </span>
                      ) : (
                        <span className='proxy-health-ok'>
                          <Icon name='check circle' />
                          {t('setting.proxy_subscription.healthy')}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>{actions(item)}</Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </div>
        ) : (
          <div className='proxy-empty-wrap'>{renderEmptyState()}</div>
        )}
        <div className='proxy-mobile-list'>
          {visible.length ? (
            visible.map((item) => (
              <article className='proxy-mobile-card' key={item.id}>
                <div className='proxy-mobile-card__head'>
                  <div>
                    <strong>{item.name}</strong>
                    <small>
                      #{item.id} · {item.detected_format || '-'}
                    </small>
                  </div>
                  <Label
                    basic
                    size='tiny'
                    color={item.enabled ? 'green' : 'grey'}
                  >
                    {item.enabled
                      ? t('setting.proxy_subscription.enabled')
                      : t('setting.proxy_subscription.disabled')}
                  </Label>
                </div>
                <code>{item.url_masked || '-'}</code>
                <div className='proxy-mobile-card__meta'>
                  <span>
                    <b>{item.node_count ?? 0}</b>
                    {t('setting.proxy_subscription.stats.nodes')}
                  </span>
                  <span>
                    <b>{formatProxyTimestamp(item.last_fetch_time)}</b>
                    {t('setting.proxy_subscription.table.last_fetch')}
                  </span>
                </div>
                {usageStats(item, 'is-mobile')}
                {item.last_error && (
                  <div className='proxy-health-error'>
                    <Icon name='warning sign' />
                    {item.last_error}
                  </div>
                )}
                {actions(item)}
              </article>
            ))
          ) : (
            renderEmptyState()
          )}
        </div>
      </section>

      <ProxySubscriptionModal
        open={modal?.type === 'create' || modal?.type === 'edit'}
        subscription={modal?.type === 'edit' ? modal.item : null}
        loading={saving}
        onClose={() => setModal(null)}
        onSubmit={save}
      />
      <ProxyNodesModal
        open={modal?.type === 'nodes'}
        subscription={modal?.type === 'nodes' ? modal.item : null}
        nodes={nodes}
        loading={nodesLoading}
        onClose={closeNodes}
      />
      <Modal
        open={!!deleteTarget}
        size='mini'
        closeIcon
        onClose={() => !busyId && setDeleteTarget(null)}
        className='studio-modal proxy-delete-modal'
      >
        <Modal.Header>
          {t('setting.proxy_subscription.delete_title')}
        </Modal.Header>
        <Modal.Content>
          <p>
            {t('setting.proxy_subscription.delete_confirm', {
              name: deleteTarget?.name,
            })}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={!!busyId} onClick={() => setDeleteTarget(null)}>
            {t('setting.proxy_subscription.cancel')}
          </Button>
          <Button
            negative
            loading={busyId === deleteTarget?.id}
            onClick={async () => {
              const target = deleteTarget;
              const ok = await remove(target);
              if (ok !== false) setDeleteTarget(null);
            }}
          >
            {t('setting.proxy_subscription.confirm_delete')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ChannelProxySetting;
